import {
  BookOpenIcon,
  CalendarIcon,
  ChevronRightIcon,
  ClipboardCheckIcon,
  ClipboardListIcon,
  DocumentTextIcon,
  FlagIcon,
  PlusIcon,
  SearchIcon,
  SparklesIcon,
  StarIcon,
  UsersIcon,
} from "@heroicons/react/outline";
import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";

import useLabel from "@apps/use-label/use-label";
import Loading from "@components/loading/loading";
import { classNames } from "@helpers/css";

export type ToolCallInfo = {
  toolName: string;
  resultCount?: number | null;
  description?: string | null;
  arguments?: string | null;
  output?: string | null;
  inFlight?: boolean;
};

const TOOL_ICONS: Record<string, typeof SearchIcon> = {
  list_goals: FlagIcon,
  list_goal_checkins: FlagIcon,
  list_action_items: ClipboardCheckIcon,
  list_feedback: DocumentTextIcon,
  list_recognitions: StarIcon,
  list_meetings: CalendarIcon,
  list_assessments: ClipboardListIcon,
  get_user_infos: UsersIcon,
  list_close_collaborators: UsersIcon,
  query_external_events: CalendarIcon,
  load_documentation: BookOpenIcon,
  load_skill: SparklesIcon,
  create_action_item: PlusIcon,
  create_recognition: PlusIcon,
  create_goal: PlusIcon,
  create_goal_checkin: PlusIcon,
  create_feedback: PlusIcon,
  add_meeting_topics: PlusIcon,
};

function getDescription(tc: ToolCallInfo): string | null {
  if (tc.description) return tc.description;
  if (!tc.arguments) return null;
  try {
    const args = JSON.parse(tc.arguments);
    return args.description || null;
  } catch {
    return null;
  }
}

function formatRawJson(raw: string | null | undefined): string {
  if (!raw) return "";
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

const ToolCallLine = ({
  tc,
  label,
  pill,
}: {
  tc: ToolCallInfo;
  label: string;
  pill: string | null;
}) => {
  const [expanded, setExpanded] = useState(false);
  const hasArguments = !!tc.arguments;
  const hasOutput = !!tc.output;
  const hasDetail = hasArguments || hasOutput;
  const Icon = TOOL_ICONS[tc.toolName] || SearchIcon;

  const row = (
    <div className="flex items-center gap-2 text-sm text-gray-500 min-w-0">
      {tc.inFlight ? (
        <Loading mini size="3.5" color="black" />
      ) : (
        <Icon className="h-3.5 w-3.5 shrink-0 text-gray-400" />
      )}
      <span className="truncate">{label}</span>
      {pill && (
        <span className="font-mono text-xs px-1.5 py-0.5 rounded-sm bg-gray-100 text-gray-600 shrink-0">
          {pill}
        </span>
      )}
    </div>
  );

  return (
    <div className="text-sm">
      {hasDetail ? (
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="block w-full text-left hover:text-gray-700 transition-colors"
        >
          {row}
        </button>
      ) : (
        row
      )}
      <AnimatePresence initial={false}>
        {expanded && hasDetail && (
          <motion.div
            key="content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="mt-1 mb-1.5 flex flex-col gap-1.5">
              {hasArguments && (
                <div>
                  <div className="text-xs uppercase tracking-wide text-gray-400 mb-0.5">
                    Request
                  </div>
                  <pre className="border rounded-sm p-1 bg-gray-50 text-xs text-gray-600 font-mono whitespace-pre-wrap wrap-break-word max-h-80 overflow-auto">
                    {formatRawJson(tc.arguments)}
                  </pre>
                </div>
              )}
              {hasOutput && (
                <div>
                  <div className="text-xs uppercase tracking-wide text-gray-400 mb-0.5">
                    Response
                  </div>
                  <pre className="border rounded-sm p-1 bg-gray-50 text-xs text-gray-600 font-mono whitespace-pre-wrap wrap-break-word max-h-80 overflow-auto">
                    {formatRawJson(tc.output)}
                  </pre>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const AskAIToolCalls = ({
  toolCalls,
  isLive = false,
}: {
  toolCalls: ToolCallInfo[];
  isLive?: boolean;
}) => {
  const [expanded, setExpanded] = useState(false);
  const label = useLabel();

  if (toolCalls.length === 0) return null;

  const getToolLabel = (tc: ToolCallInfo): string => {
    const description = getDescription(tc);
    if (description) return description;

    // Fallback labels when no description is available
    const count = tc.resultCount;
    const hasCount = count != null;

    if (tc.toolName === "list_goals") {
      const name = label("goal", { pluralize: !hasCount || count !== 1 });
      return `Fetch ${name}`;
    }
    if (tc.toolName === "list_goal_checkins") return "Fetch goal check-ins";
    if (tc.toolName === "list_action_items") return "Fetch action items";
    if (tc.toolName === "list_feedback") {
      return `Fetch ${label("feedback", { pluralize: true })}`;
    }
    if (tc.toolName === "list_recognitions") {
      return `Fetch ${label("recognition", { pluralize: true })}`;
    }
    if (tc.toolName === "list_meetings") {
      return `Fetch ${label("meeting", { pluralize: true })}`;
    }
    if (tc.toolName === "list_assessments") return "Fetch assessments";
    if (tc.toolName === "get_user_infos") return "Fetch user info";
    if (tc.toolName === "list_close_collaborators") {
      return "Fetch close collaborators";
    }
    if (tc.toolName === "query_external_events") return "Fetch external events";
    if (tc.toolName === "load_documentation") return "Load documentation";
    if (tc.toolName === "load_skill") return "Load skill";
    return tc.toolName;
  };

  const getToolPill = (tc: ToolCallInfo): string | null => {
    if (tc.resultCount != null) return String(tc.resultCount);
    return null;
  };

  const animatedItems = (
    <div className="flex flex-col gap-1.5">
      <AnimatePresence initial={isLive}>
        {toolCalls.map((tc, index) => (
          <motion.div
            key={tc.toolName + ":" + index}
            initial={{ opacity: 0, y: -4, height: 0 }}
            animate={{ opacity: 1, y: 0, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            style={{ overflow: "hidden" }}
          >
            <ToolCallLine
              tc={tc}
              label={getToolLabel(tc)}
              pill={getToolPill(tc)}
            />
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );

  if (isLive) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0, height: 0 }}
        transition={{ duration: 0.2, ease: "easeInOut" }}
        style={{ overflow: "hidden" }}
      >
        {animatedItems}
      </motion.div>
    );
  }

  return (
    <motion.div
      className="text-sm text-gray-500"
      initial={{ opacity: 0, y: -4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
    >
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 hover:text-gray-700 transition-colors"
      >
        <ChevronRightIcon
          className={classNames(
            "h-3.5 w-3.5 shrink-0 text-gray-400 transition-transform",
            expanded && "rotate-90",
          )}
        />
        <span>
          {toolCalls.length} tool call{toolCalls.length !== 1 ? "s" : ""}
        </span>
      </button>
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            key="list"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="mt-1.5">{animatedItems}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default AskAIToolCalls;
